import React, { useEffect, useState } from "react";

import {
  VerificationDetails,
  VerificationGroupType,
  VerificationStatus,
} from "../../../api/AnalysisStep";

import {
  OptionNameValuePair,
} from "../../../lib/interfaces/types";


import { IdentityProfileDocumentType, IdentityProfileDocumentTypeLabel } from "../../../../src/api/Verifications";
import EvidenceUrlModalV2 from "./EvidencemodalV2";
import { Info, Plus } from "lucide-react";
import BaseDataTable, { IColumnConfig, IDataRow } from "../../../components/ui/baseDataTable";
import ButtonV2 from "../../../components/ui/button/v2";
import { VerificationPill } from "../../../components/ui/pill/VerificationStatusPill";
import { cn } from "../../../lib/utils";
import { AnomalyPill } from "../../../components/ui/pill/AnomalyPill";
import { calculateCommonVerificationAnomaly } from "../../../lib/helpers";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import { SaveButtonV2 } from "../../../components/ui/button/v2/SaveButton";

import {v4 as uuidv4} from 'uuid';
import { addVerifications, updateVerification } from "../../../thunks/Verifications";
import { showErrorToast } from "../../../lib/toast";
import { selectIdentityVerificationsByBorrower, selectProfileVerificationsByBorrower } from "../logic/verifications/verificationSlice";
import NoDataIcon from "../../../assets/svg/EmptyTableIcon";

const IDENTITY_DOCUMENT_TYPE_OPTIONS: OptionNameValuePair<IdentityProfileDocumentType>[] = Object.values(
  IdentityProfileDocumentType
).map((value) => ({
  value,
  name: IdentityProfileDocumentTypeLabel[value],
}));

const PROFILE_DOCUMENT_TYPE_OPTIONS = Object.values(
  IdentityProfileDocumentType
).map((value) => ({
  value,
  name: IdentityProfileDocumentTypeLabel[value],
}));

const VERIFICATION_STATUS_OPTIONS :  OptionNameValuePair<VerificationStatus>[] = [
  {
    name: VerificationStatus.POSITIVE,
    display_component: <VerificationPill verfStatus={VerificationStatus.POSITIVE} />,
    value: VerificationStatus.POSITIVE,
  },
  {
    name: VerificationStatus.NEGATIVE,
    display_component: <VerificationPill verfStatus={VerificationStatus.NEGATIVE} />,
    value: VerificationStatus.NEGATIVE,
  },
  {
    name: VerificationStatus.WAIVED,
    display_component: <VerificationPill verfStatus={VerificationStatus.WAIVED} />,
    value: VerificationStatus.WAIVED,
  },
  {
    name: VerificationStatus.NOT_AVAILABLE,
    display_component: <VerificationPill verfStatus={VerificationStatus.NOT_AVAILABLE} />,
    value: VerificationStatus.NOT_AVAILABLE,
  },
];

const EmptyTableBody : React.FC<{
  onAdd: () => void
}> = ({
  onAdd
}) => (
  <div className="flex flex-col items-center justify-center py-8 px-4">
    <NoDataIcon />

    <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
      No Data Available
    </span>

    <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
      No data available for the selected Loan account
    </span>

    <ButtonV2 onClick={onAdd}>
      <Plus className="w-4 h-4 mr-1" /> Add
    </ButtonV2>
  </div>
);

export interface VerificationDetailsRow extends VerificationDetails {
  isAdded: boolean;
  isDirty: boolean;
}

interface ProfileIdentityVerificationProps {
  case_id: string;
  loanReferenceId?: string;
  borrowerId?: string;
  setHasChanges: (hasChanges: boolean) => void
}

export const ProfileIdentityVerificationContainer: React.FC<
  ProfileIdentityVerificationProps
> = ({
  case_id,
  loanReferenceId,
  borrowerId,
  setHasChanges
}) => {
  const identityVerifications = useAppSelector(state => selectIdentityVerificationsByBorrower(state, borrowerId));
  const [localIdentityData, setLocalIdentityData] = useState<VerificationDetailsRow[]>([]);

  useEffect(() => {
    setLocalIdentityData(identityVerifications.map(row => ({
      ...row,
      isAdded: false,
      isDirty: false
    })));
  }, [identityVerifications]);

  const [dirtyIdentityRows, setDirtyIdentityRows] = useState<VerificationDetailsRow[]>([]);
  const [identityEditError, setIdentityEditError] = useState<string>();
  const [hasIdentityMissingData, setHasIdentityMissingData] = useState<boolean>();

  useEffect(() => {
    const dirtyRows = localIdentityData.filter(row => row.isDirty);
    setDirtyIdentityRows(dirtyRows);
    
    const documentIds = localIdentityData.filter(row => row.document_id !== "").map(row => row.document_id)
    const hasDuplicate = (new Set(documentIds)).size < documentIds.length;
    if(hasDuplicate) {
      setIdentityEditError("Duplicate Document IDs are not allowed.")
    } else {
      setIdentityEditError(undefined);
    }

    const hasMissingData = localIdentityData.filter(row => row.isDirty).find(
      row => (row.document_id === "" || row.document_type === "")
    ) !== undefined;
    setHasIdentityMissingData(hasMissingData)

  }, [localIdentityData])

  const handleAddIdentityRow = () => {
    const newRow: VerificationDetailsRow = {
      document_type: "",
      document_id: "",
      isAdded: true,
      isDirty: true,
      onboarding_details: {},
      current_details:{},
    }
    setLocalIdentityData(prev => [...prev, newRow])
  }

  const handleDeleteIdentityRow = (rowIndex: number) => {
    setLocalIdentityData(prev => prev.filter((_, index) => index !== rowIndex));
  }

  const handleIdentityRowChange = (rowIndex: number, value: any, field: keyof VerificationDetailsRow) => {
    const existingRow = localIdentityData[rowIndex];
    var updatedRow: VerificationDetailsRow;

    if(field === "onboarding_details") {
      updatedRow = {
        ...existingRow,
        onboarding_details: {
          ...existingRow.onboarding_details,
          verification_status: value,
        },
        isDirty: true
      }
    } else if(field === "current_details"){
      updatedRow = {
        ...existingRow,
        current_details: {
          ...existingRow.current_details,
          verification_status: value
        },
        isDirty: true
      }
    } else {
      updatedRow = {
        ...existingRow,
        [field]: value,
        isDirty: true
      }
    }

    setLocalIdentityData((prev => prev.map((row, index) => index===rowIndex? updatedRow: row)))
  }

  const [identityEvidenceModalRowIndex, setIdentityEvidenceModalRowIndex] = useState<number>()

  const onIdentityEvidenceClick = (rowIndex: number) => {
    const selectedRow = localIdentityData[rowIndex];
    if(selectedRow.evidence_link){
        window.open(
        selectedRow.evidence_link,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      setIdentityEvidenceModalRowIndex(rowIndex);
    }
  }

  const handleIdentityEvidenceSave = (url: string) => {
    if(!identityEvidenceModalRowIndex) return;
    handleIdentityRowChange(identityEvidenceModalRowIndex, url, "evidence_link");
    setIdentityEvidenceModalRowIndex(undefined);
  }

  const [identityTableRows, setIdentityTableRows] = useState<IDataRow<VerificationDetailsRow>[]>([])


  useEffect(() => {
    const identityDataRows: IDataRow<VerificationDetailsRow>[] = localIdentityData.map((row) => ({
      data: row,
      isEditable: true,
      widgets: row.isAdded?['add', 'delete']:['add'],
      onAdd: handleAddIdentityRow,
      onDelete: handleDeleteIdentityRow
    }))

    setIdentityTableRows(identityDataRows);
  }, [localIdentityData]);

  const identityColumns: IColumnConfig<VerificationDetailsRow>[] = [
    {
      headerName: "Document Type",
      field: "document_type",
      type: "select",
      getValue: (row) => row.document_type,
      isEditable: (_, rowData, data_row) => rowData.isAdded,
      hideFocus: (rowData) => !rowData.isAdded,
      onChange: handleIdentityRowChange,
      options: IDENTITY_DOCUMENT_TYPE_OPTIONS,
      selectCellProps: {
        placeholder: "Document Type",
        showTooltip: true,
        showTitleSubTitle: true,
        dropdownClassName: "w-max min-w-full [&>div:first-child]:hidden",
        titleClassName: "font-sans font-medium text-sm leading-5 text-[#0A0A0A] truncate block min-w-[85px]",
        className: "font-sans font-medium text-sm text-[#0A0A0A] min-w-[85px]",
        labelClassName: "px-0 font-sans font-medium text-sm text-[#0A0A0A] block min-w-[85px] max-w-[150px] whitespace-nowrap overflow-visible",
        rootContainerClassName: "min-w-[85px] flex items-center",
      },
    },
    {
      headerName: "Document ID",
      field: "document_id",
      type: "text",
      hideFocus: (rowData) => !rowData.isAdded,
      getValue: (row) => row.document_id,
      isEditable: (_, rowData, data_row) => rowData.isAdded,
      onChange: handleIdentityRowChange,
      textCellProps: {
        className: "px-0 max-w-[100px] flex items-center",
        containerClassName: "p-0 flex items-center",
        textareaClassName: "px-0 w-[125px] flex-1 py-2 overflow-hidden whitespace-nowrap",
        numberOfRows: 1,
      },
    },
    {
      headerName: "Onboarding Status",
      field: "onboarding_details",
      type: "select",
      getValue: (row) => row.onboarding_details?.verification_status,
      isEditable: (_, rowData, data_row) => rowData.isAdded,
      hideFocus: (rowData) => !rowData.isAdded,
      onChange: handleIdentityRowChange,
      options: VERIFICATION_STATUS_OPTIONS,
      selectCellProps: {
        showTooltip: false,
        showTitleSubTitle: true,
        labelClassName: "px-0 py-1 text-xs min-w-[125px]",
        optionClassName: "px-1 w-[224px]",
        dropdownClassName: cn("w-max min-w-full", "[&>div:first-child]:hidden", "py-1"),
        className: "px-0",
        titleClassName: " font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
        rootContainerClassName: "flex items-center",
      },
    },
    {
      headerName: "Current Status",
      field: "current_details",
      type: "select",
      getValue: (row) => row.current_details?.verification_status,
      hideFocus: false,
      isEditable: true,
      onChange: handleIdentityRowChange,
      options: VERIFICATION_STATUS_OPTIONS,
      selectCellProps: {
        showTooltip: false,
        showTitleSubTitle: true,
        labelClassName: "px-0 py-1 text-xs min-w-[125px]",
        optionClassName: "px-1 w-[224px]",
        dropdownClassName: cn("w-max min-w-full", "[&>div:first-child]:hidden", "py-1"),
        titleClassName: " font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
        rootContainerClassName: "flex items-center",
      },
    },
    {
      headerName: "Anomaly",
      field: "current_details",
      type: "text",
      hideFocus: true,
      getValue: (row: VerificationDetails) => {
        return <AnomalyPill 
          anomalyLevel={calculateCommonVerificationAnomaly(row.current_details?.verification_status)} 
        />;
      },
      isEditable: false,
      textCellProps: {
        enableTooltip: false,
        className: "px-0 min-w-[85px]",
      },
    },
    {
      headerName: "Evidence",
      field: "evidence_link",
      type: "textbutton",
      hideFocus: false,
      textCellProps: {
        enableTooltip: false,
        className: "px-0 min-w-[85px]",
      },
      getValue: (row: VerificationDetails) => (
        row.evidence_link ? "Evidence" : "+ Add"
      ),
      onClick: onIdentityEvidenceClick
    },
  ]

  const profileVerifications = useAppSelector(state => selectProfileVerificationsByBorrower(state, borrowerId));
  const [localProfileData, setLocalProfileData] = useState<VerificationDetailsRow[]>([]);

  useEffect(() => {
    setLocalProfileData(profileVerifications.map(row => ({
      ...row,
      isAdded: false,
      isDirty: false
    })));
  }, [profileVerifications]);

  const [dirtyProfileRows, setDirtyProfileRows] = useState<VerificationDetailsRow[]>([]);

  const [hasProfileMissingData, setHasProfileMissingData] = useState<boolean>();


  useEffect(() => {
    setDirtyProfileRows(localProfileData.filter(row => row.isDirty));
    const hasMissingData = localProfileData.filter(row => row.isDirty).find(
      row => row.document_type===""
    ) !== undefined;
    setHasProfileMissingData(hasMissingData);
  }, [localProfileData])

  const handleAddProfileRow = () => {
    const newRow: VerificationDetailsRow = {
      document_type: "",
      document_id: uuidv4().substring(0,16),
      isAdded: true,
      isDirty: true,
      onboarding_details: {},
      current_details:{},
    }
    setLocalProfileData(prev => [...prev, newRow])
  }

  const handleDeleteProfileRow = (rowIndex: number) => {
    setLocalProfileData(prev => prev.filter((_, index) => index !== rowIndex));
  }

  const handleProfileRowChange = (rowIndex: number, value: any, field: keyof VerificationDetailsRow) => {
    const existingRow = localProfileData[rowIndex];
    var updatedRow: VerificationDetailsRow;

    if(field === "onboarding_details") {
      updatedRow = {
        ...existingRow,
        onboarding_details: {
          ...existingRow.onboarding_details,
          verification_status: value,
        },
        isDirty: true
      }
    } else if(field === "current_details"){
      updatedRow = {
        ...existingRow,
        current_details: {
          ...existingRow.current_details,
          verification_status: value
        },
        isDirty: true
      }
    } else {
      updatedRow = {
        ...existingRow,
        [field]: value,
        isDirty: true
      }
    }

    setLocalProfileData((prev => prev.map((row, index) => index===rowIndex? updatedRow: row)))
  }

  const [profileEvidenceModalRowIndex, setProfileEvidenceModalRowIndex] = useState<number>()

  const onProfileEvidenceClick = (rowIndex: number) => {
    const selectedRow = localProfileData[rowIndex];
    if(selectedRow.evidence_link){
        window.open(
        selectedRow.evidence_link,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      setProfileEvidenceModalRowIndex(rowIndex);
    }
  }

  const handleProfileEvidenceSave = (url: string) => {
    if(!profileEvidenceModalRowIndex) return;
    handleProfileRowChange(profileEvidenceModalRowIndex, url, "evidence_link");
    setProfileEvidenceModalRowIndex(undefined)
  }

  const [profileTableRows, setProfileTableRows] = useState<IDataRow<VerificationDetailsRow>[]>([])


  useEffect(() => {
    const profileDataRows: IDataRow<VerificationDetailsRow>[] = localProfileData.map((row) => ({
      data: row,
      isEditable: true,
      widgets: row.isAdded?['add', 'delete']:['add'],
      onAdd: handleAddProfileRow,
      onDelete: handleDeleteProfileRow
    }))

    setProfileTableRows(profileDataRows);
  }, [localProfileData]);

  const profileColumns: IColumnConfig<VerificationDetailsRow>[] = [
    {
      headerName: "Document Type",
      field: "document_type",
      type: "select",
      getValue: (row) => row.document_type,
      hideFocus: (rowData) => !rowData.isAdded,
      isEditable: (_, rowData, data_row) => rowData.isAdded,
      onChange: handleProfileRowChange,
      options: PROFILE_DOCUMENT_TYPE_OPTIONS,
      selectCellProps: {
        placeholder: "Document Type",
        showTooltip: true,
        showTitleSubTitle: true,
        dropdownClassName: "w-max min-w-full [&>div:first-child]:hidden",
        titleClassName: "font-sans font-medium text-sm leading-5 text-[#0A0A0A] truncate block min-w-[85px]",
        className: "font-sans font-medium text-sm text-[#0A0A0A] min-w-[85px]",
        labelClassName: "px-0 font-sans font-medium text-sm text-[#0A0A0A] block min-w-[85px] max-w-[150px] whitespace-nowrap overflow-visible",
        rootContainerClassName: "min-w-[85px] flex items-center",
      },
    },

    {
      headerName: "Onboarding Status",
      field: "onboarding_details",
      type: "select",
      getValue: (row) => row.onboarding_details?.verification_status,
      hideFocus: (rowData) => !rowData.isAdded,
      isEditable: (_, rowData, data_row) => rowData.isAdded,
      onChange: handleProfileRowChange,
      options: VERIFICATION_STATUS_OPTIONS,
      selectCellProps: {
        showTooltip: false,
        showTitleSubTitle: true,
        labelClassName: "px-0 py-1 text-xs min-w-[85px]",
        optionClassName: "px-1 w-[224px]",
        dropdownClassName: cn("w-max min-w-full", "[&>div:first-child]:hidden", "py-1"),
        className: "px-0",
        titleClassName:
          " font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
        rootContainerClassName: "flex items-center",
      },
    },

    {
      headerName: "Current Status",
      field: "current_details",
      hideFocus: false,
      type: "select" ,
      getValue: (row) => row.current_details?.verification_status,
      isEditable: true,
      onChange: handleProfileRowChange,
      options: VERIFICATION_STATUS_OPTIONS,
      selectCellProps: {
        showTooltip: false,
        showTitleSubTitle: true,
        labelClassName: "px-0 py-1 text-xs min-w-[85px]",
        optionClassName: "px-1 w-[224px]",
        dropdownClassName: cn("w-max min-w-full", "[&>div:first-child]:hidden", "py-1"),
        titleClassName: "font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
        rootContainerClassName: "flex items-center",
      },
    },
    {
      headerName: "Anomaly",
      field: "current_details",
      type: "text",
      hideFocus: true,
      getValue: (row: VerificationDetails) => {
        return <AnomalyPill 
          anomalyLevel={calculateCommonVerificationAnomaly(row.current_details?.verification_status)} 
        />;
      },
      isEditable: false,
      textCellProps: {
        enableTooltip: false,
        className: "px-0 min-w-[85px]",
      },
    },
    {
      headerName: "Evidence",
      field: "evidence_link",
      type: "textbutton",
      hideFocus: false,
      textCellProps: {
        enableTooltip: false,
        className: "px-0 min-w-[85px]",
      },
      getValue: (row: VerificationDetails) => (
        row.evidence_link ? "Evidence" : "+ Add"
      ),
      onClick: onProfileEvidenceClick
    },
  ]


  const [isSaving, setIsSaving] = useState<boolean>();

  const dispatch = useAppDispatch();
  
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const identityEditedRows = dirtyIdentityRows.filter(row => !row.isAdded);
      const identityUpdateRequests = identityEditedRows.map((row) => ({
        caseId: case_id,
        loanRefId: loanReferenceId || "",
        group: VerificationGroupType.BORROWER_IDENTITY,
        id: row.document_id,
        request: {
          current_details: row.current_details,
          evidence_link: row.evidence_link
        },
      }))

      const profileEditedRows = dirtyProfileRows.filter(row => !row.isAdded);
      const profileUpdateRequests = profileEditedRows.map((row) => ({
        caseId: case_id,
        loanRefId: loanReferenceId || "",
        group: VerificationGroupType.BORROWER_PROFILE,
        id: row.document_id,
        request: {
          current_details: row.current_details,
          evidence_link: row.evidence_link
        },
      }))

      const updateRequests = [...identityUpdateRequests, ...profileUpdateRequests]

      if(updateRequests.length > 0) {
        await Promise.all(
          updateRequests.map((updateRequest) =>
            dispatch(updateVerification(updateRequest)).unwrap()
          )
        );
      }

      const identityAddedRows = dirtyIdentityRows.filter(row => row.isAdded);
      const identityAddRequests = identityAddedRows.map((row) => ({
        loan_reference_id: loanReferenceId || "",
        customer_id: borrowerId || "",
        group: VerificationGroupType.BORROWER_IDENTITY,
        document_type: row.document_type,
        document_id: row.document_id,
        onboarding_details: row.onboarding_details || undefined,
        current_details: row.current_details || undefined,
        evidence_link: row.evidence_link,
      }))

      const profileAddedRows = dirtyProfileRows.filter(row => row.isAdded);
      const profileAddRequests = profileAddedRows.map((row) => ({
        loan_reference_id: loanReferenceId || "",
        customer_id: borrowerId || "",
        group: VerificationGroupType.BORROWER_PROFILE,
        document_type: row.document_type,
        document_id: row.document_id,
        onboarding_details: row.onboarding_details || undefined,
        current_details: row.current_details || undefined,
        evidence_link: row.evidence_link,
      }))

      const addRequests = [...identityAddRequests, ...profileAddRequests]

      if (addRequests.length > 0) {
        await dispatch(
          addVerifications({
            caseId: case_id!,
            request: {
              verifications: addRequests,
            },
          })
        ).unwrap();
      }

      setLocalIdentityData(prev => prev.map(row => ({
        ...row,
        isDirty: false,
        isAdded: false
      })))

      setLocalProfileData(prev => prev.map(row => ({
        ...row,
        isDirty: false,
        isAdded: false
      })))
      
    } catch (error) {
      console.error("Failed to save verification changes", error);
      showErrorToast("Failed to update customer document. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  const handleDiscardChanges = () => {
    setLocalIdentityData(identityVerifications.map(row => ({
      ...row,
      isDirty: false,
      isAdded: false
    })))

    setLocalProfileData(profileVerifications.map(row => ({
      ...row,
      isDirty: false,
      isAdded: false
    })))
  }

  useEffect(() => {
    setHasChanges(dirtyIdentityRows.length > 0 || dirtyProfileRows.length > 0);
  }, [dirtyIdentityRows, dirtyProfileRows])

  return (
    <div className="space-y-4">

      <div className="gap-2">
        <div className="font-semibold text-base">{'Identity Verifications'}</div>
        <div className="flex items-center mt-1">
          <Info className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
          <span className="text-xs text-gray-500">{'Authenticate borrower identity using government-issued KYC documents'}</span>
        </div> 
      </div>

      <div>

        <BaseDataTable<VerificationDetailsRow>
          columnConfigs={identityColumns}
          dataRows={identityTableRows}
          showDefaultTableBody={identityTableRows.length === 0}
          defaultTableBody={<EmptyTableBody onAdd={handleAddIdentityRow}/>}
        />

        {identityEditError && 
          <div className="mt-2 text-xs text-red-600 font-medium">
            {identityEditError}
          </div>
        }
      </div>

      {identityEvidenceModalRowIndex !== undefined && (
        <EvidenceUrlModalV2
          isOpen={identityEvidenceModalRowIndex !== undefined}
          onClose={() => {setIdentityEvidenceModalRowIndex(undefined)}}
          onSave={handleIdentityEvidenceSave}
        />
      )}

      
      <div className="gap-2">
        <div className="font-semibold text-base">{'Profile Verifications'}</div>
        <div className="flex items-center mt-1">
          <Info className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
          <span className="text-xs text-gray-500">{'Verify income or employment claims and assess creditworthiness'}</span>
        </div> 
      </div>

      <div>

        <BaseDataTable<VerificationDetailsRow>
          columnConfigs={profileColumns}
          dataRows={profileTableRows}
          showDefaultTableBody={profileTableRows.length === 0}
          defaultTableBody={<EmptyTableBody onAdd={handleAddProfileRow}/>}
        />

        {/* {profileInlineMessage && 
          <div className="mt-2 text-xs text-red-600 font-medium">
            {profileInlineMessage}
          </div>
        } */}
      </div>

      {profileEvidenceModalRowIndex !== undefined && (
        <EvidenceUrlModalV2
          isOpen={profileEvidenceModalRowIndex !== undefined}
          onClose={() => {setProfileEvidenceModalRowIndex(undefined)}}
          onSave={handleProfileEvidenceSave}
        />
      )}

      {(dirtyIdentityRows.length > 0 || dirtyProfileRows.length > 0)  && (
        <div className="bg-slate-100 flex flex-row-reverse gap-2 items-center p-2 rounded-md mt-4">
          <SaveButtonV2
            size="sm"
            buttonText="Save"
            disabled={hasProfileMissingData || hasIdentityMissingData || identityEditError !== undefined || isSaving}
            loading={isSaving}
            onClick={handleSave}
          />
          <ButtonV2
            variant="ghost"
            size="sm"
            disabled={isSaving}
            onClick={handleDiscardChanges}
          >
            Cancel
          </ButtonV2>
          <span className="text-sm font-semibold leading-5 text-[#737373]">
            {isSaving
              ? "Saving changes..."
              : "You have made changes, save now"}
          </span>
        </div>
      )}

    </div>
  );
};
